import { useState, useRef, useEffect } from 'react';
import type { RiskAssessmentConfig } from '../../../types/risk-assessment-config';

interface RiskConfigBase {
  id: string;
  name: string;
  matrixType: string;
  methodType?: string;
}

interface Props {
  inspectionId: string;
  currentConfigId?: string | null;
  effectiveConfig?: RiskConfigBase | null;
  configs: RiskAssessmentConfig[];
  canEdit: boolean;
  onSave: (configId: string | null) => Promise<void>;
  /** true 시 외부 "위험성 평가:" 레이블 숨김 — 모바일 한 줄 레이아웃용 */
  iconOnly?: boolean;
  /**
   * 트리거 생김새. 기본 `compact`(작은 테두리 버튼)는 폴드·폰이 쓴다.
   * `pill`은 데스크톱 툴바 전용 — 시안 `.filter-dd`(38px 알약 + brand 값 태그) 결이다.
   * 새 값을 기본으로 올리지 않는 이유: 이 컴포넌트를 폴드·폰도 쓰는데 그쪽 툴바는
   * 아직 시안 정렬 전이라, 기본을 바꾸면 그 화면들이 같이 끌려간다.
   */
  appearance?: 'compact' | 'pill';
}

/**
 * 이 드롭다운을 띄울지 — 세 레이아웃(desktop/fold/phone)이 공유하는 단일 규칙.
 *
 * 읽기 전용일 때는 **고를 게 없어도 띄운다.** 읽기 전용 경로는 configs를 아예 쓰지 않고
 * 효과 config 이름만 렌더하므로, 어떤 방식으로 평가했는지는 완료 후에도 보여야 한다.
 * 편집 가능할 때만 고를 후보가 있어야 의미가 있다.
 *
 * 규칙을 함수로 모은 이유: 세 레이아웃이 같은 조건을 각자 JSX에 인라인해 두고 있어서
 * 잠금 축(canEditRiskConfig)이 canEdit에서 분리될 때 게이트만 옛 축에 남아 어긋났다.
 * 그 결과 완료 점검을 여는 편집 권한자에게 configs 로딩이 끝나기 전까지(활성 config가
 * 0건이면 계속) 드롭다운이 통째로 사라졌다.
 */
export function shouldShowRiskConfigDropdown(
  configCount: number,
  canEditRiskConfig: boolean,
): boolean {
  return configCount > 0 || !canEditRiskConfig;
}

export function RiskConfigDropdown({
  currentConfigId,
  effectiveConfig,
  configs,
  canEdit,
  onSave,
  iconOnly = false,
  appearance = 'compact',
}: Props) {
  const isPill = appearance === 'pill';
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  function formatConfigLabel(
    config: { matrixType: string; name: string; methodType?: string },
    compact = false,
  ): string {
    const cleanName = config.name.replace(/\s*\([0-9xX×]+\)\s*$/, '').trim();
    if (compact) {
      if (config.methodType === 'THREE_LEVEL') return '3단계';
      if (config.matrixType === '3x3' || config.matrixType === '5x5') return config.matrixType;
      return cleanName;
    }
    if (config.matrixType === 'custom') {
      return cleanName;
    }
    return `${config.matrixType} (${cleanName})`;
  }

  const displayLabel = effectiveConfig ? formatConfigLabel(effectiveConfig, iconOnly) : '기본';

  async function handleSelect(configId: string | null) {
    setOpen(false);
    if (configId === (currentConfigId ?? null)) return;
    setSaving(true);
    try {
      await onSave(configId);
    } finally {
      setSaving(false);
    }
  }

  if (!canEdit) {
    return (
      <span className="inline-flex items-center gap-1 text-sm text-ink-500" title="위험성 평가">
        {!iconOnly && <span>위험성 평가:</span>}
        {iconOnly ? (
          <svg className="w-4 h-4 text-ink-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ) : null}
        <span className={iconOnly ? 'text-xs' : ''}>{displayLabel}</span>
      </span>
    );
  }

  return (
    <div ref={containerRef} className="relative inline-flex items-center gap-2">
      {!iconOnly && (
        <span className={isPill ? 'text-[13.5px] font-semibold text-ink-700' : 'text-sm text-ink-600'}>
          위험성 평가:
        </span>
      )}
      <button
        type="button"
        disabled={saving}
        onClick={() => setOpen((v) => !v)}
        aria-label="위험성 평가 설정"
        title="위험성 평가"
        className={
          isPill
            ? 'flex h-8 items-center gap-2 rounded-[10px] border border-line bg-white px-3 text-[13px] font-semibold text-ink-700 transition-colors hover:bg-surface-hover disabled:opacity-50'
            : `flex items-center gap-1 text-sm text-ink-600 hover:text-ink-900 rounded border border-line hover:border-ink-400 bg-white transition-colors disabled:opacity-50 ${iconOnly ? 'px-1.5 py-1' : 'px-2 py-1'}`
        }
      >
        <svg className={`flex-shrink-0 ${isPill ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <span className={iconOnly ? 'text-xs' : ''}>{saving ? '저장 중...' : displayLabel}</span>
        {/* 값 태그 — 시안 `.filter-dd .tag`. 현장 기본값인지 이 점검에서 지정했는지를 나른다. */}
        {isPill && !saving && (
          <span className="rounded-[5px] bg-brand-50 px-1.5 py-px text-[12.5px] font-bold tracking-[.01em] text-brand-700">
            {currentConfigId ? '지정' : '기본'}
          </span>
        )}
        <svg className={isPill ? 'h-[13px] w-[13px] text-ink-400' : 'h-3 w-3'} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-line rounded-card shadow-card min-w-[180px]">
          {/* 현장 기본값으로 복원 */}
          <button
            type="button"
            onClick={() => handleSelect(null)}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-hover ${
              !currentConfigId ? 'font-semibold text-brand-600' : 'text-ink-700'
            }`}
          >
            현장 기본값
          </button>
          <div className="border-t border-line-soft" />
          {configs.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => handleSelect(c.id)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-hover ${
                currentConfigId === c.id ? 'font-semibold text-brand-600' : 'text-ink-700'
              }`}
            >
              {formatConfigLabel(c, iconOnly)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
